import { Toaster } from '@mdvip/shared/components/ui/toaster';
import { IdleTimeoutManager } from '@mdvip/shared/features/idle-timeout/IdleTimeoutManager';
import { AuthCallbackPage } from '@mdvip/shared/pages/AuthCallbackPage';
import { LoginPage } from '@mdvip/shared/pages/LoginPage';
import { NotFoundPage } from '@mdvip/shared/pages/NotFoundPage';
import { ApiPassthrough } from '@mdvip/shared/system/ApiPassthrough';
import { DeepLinkAuthHandler } from '@mdvip/shared/system/DeepLinkAuthHandler';
import { getHealthUrl } from '@mdvip/shared/types/config';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { HomeRedirect } from './components/HomeRedirect';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { useConfig } from './contexts/ConfigContext';
import { DEFAULT_PATIENTS_TAB } from './lib/patients/patientsTabs';
import { AwpProgramPage } from './pages/AwpProgramPage';
import { PracticePage } from './pages/PracticePage';
import { MyPatientDirectoryPage } from './pages/patients/MyPatientDirectoryPage';
import { PatientsPage } from './pages/patients/PatientsPage';
import { ReportsPage } from './pages/ReportsPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { StaffManagementPage } from './pages/StaffManagementPage';

function App() {
  const config = useConfig();

  return (
    <>
      <IdleTimeoutManager
        idleTimeoutMs={config.IDLE_TIMEOUT_MS}
        warningDurationMs={config.WARNING_DURATION_MS}
        tokenRefreshThreshold={config.TOKEN_REFRESH_THRESHOLD}
      />
      <BrowserRouter>
        <DeepLinkAuthHandler />
        <Routes>
          {/* Front Door routes /api/* to the frontline APIM; the SPA must never handle it. */}
          <Route path="/api/*" element={<ApiPassthrough fallback={<NotFoundPage />} />} />
          <Route path="/login" element={<LoginPage healthUrl={getHealthUrl(config)} postLoginRedirect="/" />} />
          <Route path="/auth/callback" element={<AuthCallbackPage postLoginRedirect="/" />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/patients"
              element={
                <ProtectedRoute can={['read', 'Member']}>
                  <PatientsPage />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to={DEFAULT_PATIENTS_TAB} replace />} />
              <Route path="my-patient-directory" element={<MyPatientDirectoryPage />} />
              {/* Keeps every /patients URL behind the guard above; without it an unknown or
                  not-yet-shipped tab would fall through to the top-level 404 unchecked. Absolute,
                  unlike the index: a relative target resolves against the splat URL itself, so
                  it would redirect to another unmatched path and loop. */}
              <Route path="*" element={<Navigate to={`/patients/${DEFAULT_PATIENTS_TAB}`} replace />} />
            </Route>
            <Route
              path="/awp"
              element={
                <ProtectedRoute can={['read', 'Member']}>
                  <AwpProgramPage />
                </ProtectedRoute>
              }
            />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/staff" element={<StaffManagementPage />} />
            {/* Not in the primary navigation or the account menu — the physician design has no
                slot for it yet, so it stays reachable by URL only. */}
            <Route path="/practice" element={<PracticePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <Toaster />
      </BrowserRouter>
    </>
  );
}

export default App;
